import { useTranslation } from 'react-i18next';
import { formatter } from '@repo/shared';
import type { ColumnDef, Row } from '@tanstack/react-table';
import { GitCommitVertical, MessageCircle } from 'lucide-react';

import type { ThreadsQueryResult } from '@/api/generated';
import { ColumnHeader } from '@/components/blocks/data-table/column-header';
import { Button } from '@/components/ui/button';
import FeedbackBadge from '@/routes/(app)/agents/components/feedback-badge';

type Thread = ThreadsQueryResult['data'][number];

interface ThreadsColumnsProps {
  onThreadTracing: (row: Row<Thread>) => void;
  onThreadFeedback: (row: Row<Thread>) => void;
}

export const columns = ({
  onThreadTracing,
  onThreadFeedback,
}: ThreadsColumnsProps): Array<ColumnDef<Thread>> => [
  {
    accessorKey: 'id',
    header: ({ column }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('feedbackAgent.table.id')}
        />
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: 'amountOfMessages',
    header: ({ column }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('feedbackAgent.table.amountOfMessages')}
        />
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: 'title',
    header: ({ column }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('feedbackAgent.table.title')}
        />
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: 'feedback',
    header: ({ column }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('feedbackAgent.table.feedback')}
        />
      );
    },
    cell: ({ row }) =>
      row.original.feedback && (
        <FeedbackBadge feedback={row.original.feedback} />
      ),
    enableSorting: false,
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('feedbackAgent.table.createdAt')}
        />
      );
    },
    cell: ({ row }) =>
      formatter.date.dateTime(new Date(row.original.createdAt as string)),
  },
  {
    id: 'view-tracing',
    header: () => null,
    cell: ({ row }) => (
      <Button
        variant={'ghost'}
        onClick={() => {
          onThreadTracing(row);
        }}>
        <Button.Icon icon>
          <GitCommitVertical />
        </Button.Icon>
      </Button>
    ),
  },
  {
    id: 'view-feedback',
    header: () => null,
    cell: ({ row }) => (
      <Button
        variant={'ghost'}
        onClick={() => {
          onThreadFeedback(row);
        }}>
        <Button.Icon icon>
          <MessageCircle />
        </Button.Icon>
      </Button>
    ),
  },
];
